import axios, { AxiosRequestConfig } from 'axios';

import { getParamSerializer } from 'app/api/serializer';
import { TokenStore } from 'app/api/TokenStore';
import SessionStorageTokenStore from 'app/auth/SessionStorageTokenStore';
import { createErrorInterceptors } from 'app/api/interceptor';
import { NetworkErrorInterceptor } from 'app/api/interceptor/NetworkErrorInterceptor';
import { ForbiddenErrorInterceptor } from 'app/api/interceptor/ForbiddenErrorInterceptor';
import { NotFoundErrorInterceptor } from 'app/api/interceptor/NotFoundErrorInterceptor';
import { UnauthorizedErrorInterceptor } from 'app/api/interceptor/UnauthorizedErrorInteceptor';
import { ValidationErrorInterceptor } from 'app/api/interceptor/ValidationErrorInterceptor';

/**
 * Возвращает данные для Authorization заголовка в формате 'Bearer ${token}'.
 *
 * @param token access token
 */
const getAuthorizationHeaderData = (token: string): string => {
    return `Bearer ${token}`;
};

export default class AxiosHttpClient {
    constructor(
        private readonly BACKEND_URL: string = (window as any).env.BACKEND_URL,
        private readonly tokenStore: TokenStore = SessionStorageTokenStore,
    ) {}

    /**
     * Возвращает настройки запроса.
     *
     * @param config настройки для axios
     * @param withAuthorization будет ли добавлен заголовок Authorization к запросу
     * @param repeatableRequest будет ли выполнена повторная попытка запроса данных с новым токеном, если произойдет 401 ошибка
     * @param indices параметр, указывающий на метод сериализации списков в виде item[0]=value&item[1]=value...
     */
    private getRequestConfig(
        config?: AxiosRequestConfig,
        withAuthorization: boolean = true,
        repeatableRequest: boolean = true,
        indices: boolean = false,
    ): any {
        const headers: any = { 'Content-Type': 'application/json' };

        const token = this.tokenStore.token;

        if (token && withAuthorization) {
            headers.Authorization = getAuthorizationHeaderData(token);
        }

        return {
            baseURL: this.BACKEND_URL,
            paramsSerializer: getParamSerializer(indices),
            // transformResponse: [function (data: any) {
            //     if (typeof data === 'string') {
            //         try {
            //             data = jsonBigInt.parse(data);
            //         } catch (e) { /* Ignore */ }
            //     }
            //     return data;
            // }],
            ...config,
            headers: {
                ...headers,
                ...config?.headers,
            },
            repeatableRequest,
        };
    }

    public async executeGetRequest<T = any>(
        url: string,
        params: any = null,
        indices: boolean = false,
    ): Promise<T> {
        const response = await axios.get(
            url,
            this.getRequestConfig({ params }, true, true, indices),
        );

        return response.data;
    }

    public async executePostRequest(
        url: string,
        body: any,
        params: any = null,
        baseURL: string = this.BACKEND_URL,
    ): Promise<any> {
        const response = await axios.post(
            url,
            body,
            this.getRequestConfig({ params, baseURL }),
        );
        return response.data;
    }

    public async executePutRequest(url: string, body: any): Promise<any> {
        const response = await axios.put(url, body, this.getRequestConfig());
        return response.data;
    }

    public async executeDeleteRequest(url: string) {
        const response = await axios.delete(url, this.getRequestConfig());

        return response.data;
    }

    public async executeUpload(url: string, file: File | Blob) {
        const config: AxiosRequestConfig = {
            headers: { 'Content-Type': file.type },
        };
        const response = await axios.post(
            url,
            file,
            this.getRequestConfig(config),
        );
        return response.data;
    }

    public async executeDownload(
        url: string,
        params: any = null,
    ): Promise<{ data: any; headers: any }> {
        const config: AxiosRequestConfig = {
            responseType: 'arraybuffer',
            params,
            paramsSerializer: getParamSerializer(true),
        };

        const response = await axios.get(url, this.getRequestConfig(config));

        return {
            data: response.data,
            headers: response.headers,
        };
    }

    /**
     * Выполняет post выполняет запросы связанные с токеном к ABO.
     *
     * @param url ABO url
     * @param body параметры запроса
     * @param params
     * @param baseURL
     */
    public async executeTokenRequest(
        url: string,
        body: any,
        params: any = null,
        baseURL: string = this.BACKEND_URL,
    ): Promise<any> {
        const response = await axios.post(
            url,
            body,
            this.getRequestConfig({ params, baseURL }, false),
        );
        return response.data;
    }

    /**
     * Выполняет POST запрос связанный с логгированием клиентских событий на сервер.
     * Так как логгирование разрешено только для авторизованных пользователей, осуществляется проверка на наличие токена
     *
     * @param url
     * @param body
     * @param params
     * @param baseURL
     */
    public async executeLoggerRequest(
        url: string,
        body: any,
        params: any = null,
        baseURL: string = this.BACKEND_URL,
    ): Promise<any> {
        if (!this.tokenStore.tokenExists) {
            return;
        }

        const response = await axios.post(
            url,
            body,
            this.getRequestConfig({ params, baseURL }),
        );
        return response.data;
    }
}

axios.interceptors.response.use(
    (response) => {
        return response;
    },
    createErrorInterceptors([
        new NetworkErrorInterceptor(),
        new NotFoundErrorInterceptor(),
        new UnauthorizedErrorInterceptor(),
        new ForbiddenErrorInterceptor(),
        new ValidationErrorInterceptor(),
    ]),
);
